import { Controller } from "@hotwired/stimulus"

const STORAGE_PREFIX = "form-persist:"
const SAVE_DEBOUNCE_MS = 300
const EXCLUDED_TYPES = new Set(["file", "password", "submit", "button", "image", "reset"])
const EXCLUDED_NAMES = new Set(["authenticity_token"])
const SHARED_KEY = "form-persist:shared"

export default class extends Controller {
  static values = {
    storageKey: String,
    expiryMinutes: { type: Number, default: 15 },
    // Default true preserves original "clear on submit" semantics for forms
    // that aren't re-rendered server-side after validation failure (e.g.,
    // contact, hello). Forms that DO get re-rendered (e.g., public booking
    // wizard) set this to false so the customer's data survives the
    // server-side validation round-trip and is restored on render :new.
    clearOnSubmit: { type: Boolean, default: true },
  }

  connect() {
    if (!this.storageKeyValue) return

    this.boundOnInput = this.onInput.bind(this)
    this.boundOnChange = this.onChange.bind(this)
    this.boundOnSubmit = this.onSubmit.bind(this)
    this.boundOnMutation = this.onMutation.bind(this)

    this.element.addEventListener("input", this.boundOnInput)
    this.element.addEventListener("change", this.boundOnChange)
    this.element.addEventListener("submit", this.boundOnSubmit)

    this.mutationObserver = new MutationObserver(this.boundOnMutation)
    this.mutationObserver.observe(this.element, { childList: true, subtree: true })

    if (document.readyState === "loading") {
      requestAnimationFrame(() => this.restore())
    } else {
      this.restore()
    }
  }

  disconnect() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    if (this.mutationObserver) this.mutationObserver.disconnect()
    if (this.boundOnInput)  this.element.removeEventListener("input",  this.boundOnInput)
    if (this.boundOnChange) this.element.removeEventListener("change", this.boundOnChange)
    if (this.boundOnSubmit) this.element.removeEventListener("submit", this.boundOnSubmit)
  }

  // ── Event handlers ──────────────────────────────────────────

  onInput(event) {
    if (!this.shouldPersist(event.target)) return
    this.scheduleSave()
  }

  onChange(event) {
    if (!this.shouldPersist(event.target)) return
    if (this.isTextInput(event.target)) return
    this.save()
  }

  onSubmit() {
    // Forms that get re-rendered server-side after validation failure (the
    // public booking wizard) opt out of this clear so the customer doesn't
    // lose their state on the round-trip. Forms that just submit-and-leave
    // (contact, hello) keep the original clear behavior. Shared identity
    // store is preserved either way (cross-form pre-fill via TTL expiry).
    if (!this.clearOnSubmitValue) return
    this.clearStore(this.formLocalKey())
  }

  onMutation() {
    // Re-save (don't re-populate — wizards inject hidden values we shouldn't clobber).
    this.scheduleSave()
  }

  isExpired(stored) {
    if (!stored || !stored.savedAt) return false
    const savedAt = Date.parse(stored.savedAt)
    if (Number.isNaN(savedAt)) return true
    const ageMs = Date.now() - savedAt
    return ageMs > this.expiryMinutesValue * 60_000
  }

  // ── Persistence ─────────────────────────────────────────────

  scheduleSave() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => this.save(), SAVE_DEBOUNCE_MS)
  }

  save() {
    const fields = {}
    const sharedFields = {}
    this.persistableFields().forEach(input => {
      const value = this.readValue(input)
      fields[input.name] = value
      const sharedKey = input.dataset.formPersistSharedKey
      if (sharedKey) sharedFields[sharedKey] = value
    })
    this.writeStore(this.formLocalKey(), { savedAt: new Date().toISOString(), fields })
    if (Object.keys(sharedFields).length > 0) {
      const existing = this.readStore(SHARED_KEY) || { fields: {} }
      const merged = { ...existing.fields, ...sharedFields }
      this.writeStore(SHARED_KEY, { savedAt: new Date().toISOString(), fields: merged })
    }
  }

  restore() {
    const stored = this.readStore(this.formLocalKey())
    if (stored && stored.fields) {
      if (this.isExpired(stored)) {
        this.clearStore(this.formLocalKey())
      } else {
        Object.entries(stored.fields).forEach(([name, value]) => {
          const input = this.element.querySelector(`[name="${CSS.escape(name)}"]`)
          if (input && this.shouldPersist(input)) this.writeValue(input, value)
        })
      }
    }

    const shared = this.readStore(SHARED_KEY)
    if (shared && shared.fields) {
      if (this.isExpired(shared)) {
        this.clearStore(SHARED_KEY)
      } else {
        this.element.querySelectorAll("[data-form-persist-shared-key]").forEach(input => {
          if (!this.shouldPersist(input)) return
          const sharedKey = input.dataset.formPersistSharedKey
          const value = shared.fields[sharedKey]
          if (value !== undefined && value !== null && value !== "") this.writeValue(input, value)
        })
      }
    }
  }

  // ── Field selection ─────────────────────────────────────────

  persistableFields() {
    return Array.from(this.element.querySelectorAll("input[name], textarea[name], select[name]"))
      .filter(input => this.shouldPersist(input))
  }

  shouldPersist(input) {
    if (!input || !input.name) return false
    if (EXCLUDED_NAMES.has(input.name)) return false
    if (input.dataset.formPersistSkip !== undefined) return false
    const type = (input.type || "").toLowerCase()
    if (EXCLUDED_TYPES.has(type)) return false
    if (type === "hidden" && input.dataset.formPersistInclude === undefined) return false
    return true
  }

  isTextInput(input) {
    if (input.tagName === "TEXTAREA") return true
    const type = (input.type || "").toLowerCase()
    return ["text", "email", "tel", "url", "search", "number", "password"].includes(type)
  }

  readValue(input) {
    if (input.type === "checkbox") return input.checked
    if (input.type === "radio") return input.checked ? input.value : null
    return input.value
  }

  writeValue(input, value) {
    if (value === null || value === undefined) return
    if (input.type === "checkbox") { input.checked = !!value; return }
    if (input.type === "radio")    { input.checked = (input.value === value); return }
    input.value = value
  }

  // ── Storage primitives ──────────────────────────────────────

  formLocalKey() {
    return `${STORAGE_PREFIX}${this.storageKeyValue}`
  }

  readStore(key) {
    try {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }

  writeStore(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // localStorage unavailable (private mode, quota); fail silently.
    }
  }

  clearStore(key) {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore
    }
  }
}
